import React, { useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider
} from '@mui/material';
import {
  Print,
  Close,
  Download,
  WhatsApp
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';
import '../styles/receipt.css';

const ReceiptModal = ({ open, onClose, sale }) => {
  const printRef = useRef();

  if (!sale) return null;

  // Generar datos para QR
  const generateQRData = () => {
    return JSON.stringify({
      venta: sale.saleNumber,
      fecha: new Date(sale.saleDate).toLocaleDateString('es-AR'),
      total: parseFloat(sale.total),
      items: sale.items?.length || 0
    });
  };

  // Descargar como PDF
  const handleDownloadPDF = async () => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 10;
    let yPos = 15;
    
    // Función helper para agregar texto
    const addText = (text, fontSize = 10, align = 'left', isBold = false) => {
      pdf.setFontSize(fontSize);
      if (isBold) pdf.setFont(undefined, 'bold');
      else pdf.setFont(undefined, 'normal');
      
      if (align === 'center') {
        pdf.text(text, pageWidth / 2, yPos, { align: 'center' });
      } else if (align === 'right') {
        pdf.text(text, pageWidth - margin, yPos, { align: 'right' });
      } else {
        pdf.text(text, margin, yPos);
      }
      yPos += fontSize * 0.5;
    };

    const addLine = () => {
      pdf.setDrawColor(200);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 3;
    };

    try {
      // Logo de la empresa (si existe)
      try {
        const logoImg = new Image();
        logoImg.src = '/logo.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = () => {
            const imgWidth = 40;
            const imgHeight = (logoImg.height * imgWidth) / logoImg.width;
            const xPos = (pageWidth - imgWidth) / 2;
            pdf.addImage(logoImg, 'PNG', xPos, yPos, imgWidth, imgHeight);
            yPos += imgHeight + 5;
            resolve();
          };
          logoImg.onerror = () => {
            // Si no hay logo, continuar sin error
            resolve();
          };
          // Timeout para no esperar indefinidamente
          setTimeout(() => resolve(), 1000);
        });
      } catch (e) {
        // Continuar sin logo
      }
      
      // Encabezado
      addText(getTipoComprobanteLabel(sale.tipoComprobanteId), 16, 'center', true);
      yPos += 2;
      addText(`N° ${sale.saleNumber}`, 12, 'center');
      yPos += 5;
      addLine();
      
      // Información de la venta
      addText(`Fecha: ${new Date(sale.saleDate).toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`, 10);
      
      if (sale.customerName) {
        addText(`Cliente: ${sale.customerName}`, 10);
      }
      
      if (sale.customerDocument) {
        addText(`Documento: ${sale.customerDocument}`, 10);
      }
      
      addText(`Pago: ${getPaymentMethodLabel(sale.paymentMethod)}`, 10);
      
      if (sale.user) {
        addText(`Vendedor: ${sale.user.fullName || sale.user.username}`, 10);
      }
      
      yPos += 3;
      addLine();
      
      // Productos - Encabezado
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(9);
      pdf.text('PRODUCTO', margin, yPos);
      pdf.text('CANT', pageWidth - 60, yPos);
      pdf.text('PRECIO', pageWidth - 45, yPos);
      pdf.text('TOTAL', pageWidth - margin, yPos, { align: 'right' });
      pdf.setFont(undefined, 'normal');
      yPos += 5;
      
      // Items
      pdf.setFontSize(9);
      if (sale.items && sale.items.length > 0) {
        sale.items.forEach((item) => {
          const productName = item.productName || item.product?.name || 'Producto';
          const quantity = item.quantity;
          const price = parseFloat(item.unitPrice);
          const subtotal = parseFloat(item.subtotal);
          
          // Verificar espacio en la página
          if (yPos > 270) {
            pdf.addPage();
            yPos = 15;
          }
          
          pdf.text(productName.substring(0, 30), margin, yPos);
          pdf.text(quantity.toString(), pageWidth - 60, yPos);
          pdf.text(`$${price.toLocaleString('es-AR')}`, pageWidth - 45, yPos);
          pdf.text(`$${subtotal.toLocaleString('es-AR')}`, pageWidth - margin, yPos, { align: 'right' });
          yPos += 5;
        });
      }
      
      yPos += 2;
      addLine();
      
      // Totales
      pdf.setFontSize(10);
      pdf.text('Subtotal:', margin, yPos);
      pdf.text(`$${parseFloat(sale.subtotal).toLocaleString('es-AR')}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 5;
      
      if (sale.discount > 0) {
        pdf.text('Descuento:', margin, yPos);
        pdf.text(`-$${parseFloat(sale.discount).toLocaleString('es-AR')}`, pageWidth - margin, yPos, { align: 'right' });
        yPos += 5;
      }
      
      yPos += 2;
      pdf.setDrawColor(0);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(14);
      pdf.text('TOTAL:', margin, yPos);
      pdf.text(`$${parseFloat(sale.total).toLocaleString('es-AR')}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 8;
      
      pdf.setFont(undefined, 'normal');
      
      // Notas
      if (sale.notes) {
        yPos += 3;
        pdf.setFontSize(9);
        pdf.text('Notas:', margin, yPos);
        yPos += 4;
        pdf.setFontSize(8);
        const splitNotes = pdf.splitTextToSize(sale.notes, pageWidth - 2 * margin);
        pdf.text(splitNotes, margin, yPos);
        yPos += splitNotes.length * 4;
      }
      
      // Código QR
      yPos += 10;
      try {
        // Crear canvas temporal para el QR
        const canvas = document.createElement('canvas');
        const QRCode = (await import('qrcode')).default;
        
        await QRCode.toCanvas(canvas, generateQRData(), {
          width: 200,
          margin: 2
        });
        
        const qrDataUrl = canvas.toDataURL('image/png');
        const qrSize = 35;
        const qrX = (pageWidth - qrSize) / 2;
        
        pdf.addImage(qrDataUrl, 'PNG', qrX, yPos, qrSize, qrSize);
        yPos += qrSize + 5;
      } catch (e) {
        console.error('Error al generar QR:', e);
      }
      
      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(100);
      pdf.text('¡Gracias por su compra!', pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
      pdf.text('Este comprobante no es válido como factura fiscal', pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
      pdf.text('Escanea el QR para ver detalles de la venta', pageWidth / 2, yPos, { align: 'center' });
      
      // Guardar PDF
      pdf.save(`Recibo-${sale.saleNumber}.pdf`);
      
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('Error al generar el PDF. Por favor, intente nuevamente.');
    }
  };

  // Compartir por WhatsApp
  const handleShareWhatsApp = () => {
    const message = `*RECIBO DE VENTA*\n\n` +
      `*Número:* ${sale.saleNumber}\n` +
      `*Fecha:* ${new Date(sale.saleDate).toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}\n` +
      (sale.customerName ? `*Cliente:* ${sale.customerName}\n` : '') +
      `*Método de Pago:* ${getPaymentMethodLabel(sale.paymentMethod)}\n\n` +
      `*DETALLE DE PRODUCTOS:*\n` +
      (sale.items?.map(item => 
        `- ${item.productName || item.product?.name} x${item.quantity} - $${parseFloat(item.subtotal).toLocaleString('es-AR')}`
      ).join('\n') || '') +
      `\n\n*Subtotal:* $${parseFloat(sale.subtotal).toLocaleString('es-AR')}\n` +
      (sale.discount > 0 ? `*Descuento:* -$${parseFloat(sale.discount).toLocaleString('es-AR')}\n` : '') +
      `*TOTAL:* $${parseFloat(sale.total).toLocaleString('es-AR')}\n\n` +
      `_¡Gracias por su compra!_`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const windowPrint = window.open('', '', 'width=800,height=600');
    
    windowPrint.document.write(`
      <html>
        <head>
          <title>Recibo - ${sale.saleNumber}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Courier New', monospace;
              padding: 20px;
              max-width: 80mm;
              margin: 0 auto;
            }
            .receipt-header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px dashed #000;
              padding-bottom: 10px;
            }
            .receipt-title {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .receipt-info {
              margin-bottom: 15px;
              font-size: 12px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin: 5px 0;
            }
            .receipt-items {
              margin: 15px 0;
            }
            .item-row {
              display: flex;
              justify-content: space-between;
              margin: 8px 0;
              font-size: 12px;
            }
            .item-name {
              flex: 1;
            }
            .item-qty {
              width: 60px;
              text-align: center;
            }
            .item-price {
              width: 80px;
              text-align: right;
            }
            .receipt-totals {
              border-top: 2px dashed #000;
              padding-top: 10px;
              margin-top: 10px;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              margin: 5px 0;
              font-size: 13px;
            }
            .total-row.final {
              font-size: 16px;
              font-weight: bold;
              margin-top: 10px;
            }
            .receipt-footer {
              text-align: center;
              margin-top: 20px;
              border-top: 2px dashed #000;
              padding-top: 10px;
              font-size: 11px;
            }
            .table-header {
              border-bottom: 1px solid #000;
              padding-bottom: 5px;
              margin-bottom: 5px;
              font-weight: bold;
              font-size: 12px;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    windowPrint.document.close();
    windowPrint.focus();
    
    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 250);
  };

  const getTipoComprobanteLabel = (tipoId) => {
    const tipos = {
      0: 'RECIBO / TICKET',
      1: 'FACTURA A',
      6: 'FACTURA B',
      11: 'FACTURA C'
    };
    return tipos[tipoId] || 'RECIBO';
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      'efectivo': 'Efectivo',
      'tarjeta': 'Tarjeta',
      'transferencia': 'Transferencia',
      'mercadopago': 'MercadoPago'
    };
    return methods[method] || method;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box className="d-flex justify-between align-center">
          <Typography variant="h6">Recibo de Venta</Typography>
          <Typography variant="body2" color="text.secondary">
            {sale.saleNumber}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box ref={printRef} className="receipt-content">
          {/* Encabezado con Logo */}
          <Box className="receipt-header">
            {/* Logo de la empresa */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img 
                src="/logo.png" 
                alt="Logo" 
                style={{ 
                  maxWidth: '120px', 
                  maxHeight: '60px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
            
            <Typography className="receipt-title">
              {getTipoComprobanteLabel(sale.tipoComprobanteId)}
            </Typography>
            <Typography variant="body2">
              Nº {sale.saleNumber}
            </Typography>
          </Box>

          {/* Información de la venta */}
          <Box className="receipt-info">
            <Box className="info-row">
              <Typography variant="body2">Fecha:</Typography>
              <Typography variant="body2">
                {new Date(sale.saleDate).toLocaleDateString('es-AR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>

            {sale.customerName && (
              <Box className="info-row">
                <Typography variant="body2">Cliente:</Typography>
                <Typography variant="body2">{sale.customerName}</Typography>
              </Box>
            )}

            {sale.customerDocument && (
              <Box className="info-row">
                <Typography variant="body2">Documento:</Typography>
                <Typography variant="body2">{sale.customerDocument}</Typography>
              </Box>
            )}

            <Box className="info-row">
              <Typography variant="body2">Pago:</Typography>
              <Typography variant="body2">
                {getPaymentMethodLabel(sale.paymentMethod)}
              </Typography>
            </Box>

            {sale.user && (
              <Box className="info-row">
                <Typography variant="body2">Vendedor:</Typography>
                <Typography variant="body2">
                  {sale.user.fullName || sale.user.username}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Items */}
          <Box className="receipt-items">
            <Box className="table-header" sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ flex: 1 }}>Producto</Typography>
              <Typography variant="body2" sx={{ width: '60px', textAlign: 'center' }}>Cant.</Typography>
              <Typography variant="body2" sx={{ width: '80px', textAlign: 'right' }}>Precio</Typography>
              <Typography variant="body2" sx={{ width: '80px', textAlign: 'right' }}>Subtotal</Typography>
            </Box>

            {sale.items && sale.items.map((item, index) => (
              <Box key={index} className="item-row" sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                  {item.productName || item.product?.name}
                </Typography>
                <Typography variant="body2" sx={{ width: '60px', textAlign: 'center' }}>
                  {item.quantity}
                </Typography>
                <Typography variant="body2" sx={{ width: '80px', textAlign: 'right' }}>
                  ${parseFloat(item.unitPrice).toLocaleString('es-AR')}
                </Typography>
                <Typography variant="body2" sx={{ width: '80px', textAlign: 'right' }}>
                  ${parseFloat(item.subtotal).toLocaleString('es-AR')}
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Totales */}
          <Box className="receipt-totals">
            <Box className="total-row" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Subtotal:</Typography>
              <Typography variant="body2">
                ${parseFloat(sale.subtotal).toLocaleString('es-AR')}
              </Typography>
            </Box>

            {sale.discount > 0 && (
              <Box className="total-row" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="success.main">Descuento:</Typography>
                <Typography variant="body2" color="success.main">
                  -${parseFloat(sale.discount).toLocaleString('es-AR')}
                </Typography>
              </Box>
            )}

            <Box className="total-row final" sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid #ddd' }}>
              <Typography variant="h6" fontWeight="bold">TOTAL:</Typography>
              <Typography variant="h6" fontWeight="bold">
                ${parseFloat(sale.total).toLocaleString('es-AR')}
              </Typography>
            </Box>
          </Box>

          {/* Notas */}
          {sale.notes && (
            <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Notas:</Typography>
              <Typography variant="body2">{sale.notes}</Typography>
            </Box>
          )}

          {/* Footer con QR */}
          <Box className="receipt-footer" sx={{ mt: 3, pt: 2, borderTop: '2px dashed #ddd', textAlign: 'center' }}>
            {/* Código QR */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <QRCodeSVG 
                value={generateQRData()} 
                size={100}
                level="M"
                includeMargin={true}
              />
            </Box>
            
            <Typography variant="caption" display="block">
              ¡Gracias por su compra!
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              Este comprobante no es válido como factura fiscal
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Escanea el QR para ver detalles de la venta
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
        <Button
          onClick={handlePrint}
          startIcon={<Print />}
          variant="contained"
          color="primary"
        >
          Imprimir
        </Button>
        <Button
          onClick={handleDownloadPDF}
          startIcon={<Download />}
          variant="contained"
          color="secondary"
        >
          Descargar PDF
        </Button>
        <Button
          onClick={handleShareWhatsApp}
          startIcon={<WhatsApp />}
          variant="contained"
          sx={{ 
            bgcolor: '#25D366',
            '&:hover': { bgcolor: '#20BA5A' }
          }}
        >
          WhatsApp
        </Button>
        <Button
          onClick={onClose}
          startIcon={<Close />}
          variant="outlined"
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptModal;

